import { usePrompt, useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'

import { useQuery } from '@apollo/client'
import type { AllUserDocumentCategoriesQuery } from '@/modules/users'
import { ALL_USER_DOCUMENT_CATEGORIES_QUERY } from '@/modules/users/queries'
import React, { useEffect, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useConfirm, useOnErrorAuto } from '@/utils/hooks'
import type {
  UserDocumentCategoriesSettingsForm,
  UserDocumentCategoryForSettings,
} from '../components'
import {
  OldAddButton,
  InfoBox,
  OldSection,
  SectionHeader,
  SettingsWrapper,
} from '../components/common'
import type { UserDocumentCategoryForm } from '../components/UserDocumentSettings/UserDocumentCategoryPrompt'
import { UserDocumentCategoryPrompt } from '../components/UserDocumentSettings/UserDocumentCategoryPrompt'
import { UserDocumentCategorySettingsCard } from '../components/UserDocumentSettings/UserDocumentCategorySettingsCard'
import { UserDocumentCategorySettingsTable } from '../components/UserDocumentSettings/UserDocumentCategorySettingsTable'
import { useUserDocumentCategoryMutations } from '../util/mutations.hooks'

export const UserDocumentCategorySettings: React.FC = () => {
  const translations = useTranslate({
    info: 'settings.info.user-document-categories',

    prompt: {
      delete: ['settings.prompts.user-document-delete', { name: '' }],
      deleteTitle: 'settings.prompts.user-document-delete-title',
    },
    validation: {
      nameExists: 'settings.validation.user-document-name-exists',
    },
    userDocumentCategories: 'common.user-document-categories',
  })

  const confirm = useConfirm()
  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const categoryMutations = useUserDocumentCategoryMutations()

  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)

  const addToast = useToast()

  const { data: categoryData, loading } =
    useQuery<AllUserDocumentCategoriesQuery>(
      ALL_USER_DOCUMENT_CATEGORIES_QUERY,
      {
        onError: onErrorAuto(),
      }
    )

  const editableCategories =
    categoryData?.allUserDocumentCategories.edges.map(edge => edge.node) ?? []

  const {
    formValues: form,
    formChangeHandler: handler,
    updateForm,
  } = useForm<UserDocumentCategoriesSettingsForm>({
    values: {
      newName: '',
      editName: '',
    },
  })

  async function submitNewUserDocumentCategory() {
    if (!validateName(form.newName)) return

    await categoryMutations.create({
      variables: {
        input: {
          name: form.newName,
        },
      },
    })
    setAdding(false)
  }

  async function handleCreatePrompt() {
    const { data } = await addPrompt<UserDocumentCategoryForm | null>(
      resolve => (
        <UserDocumentCategoryPrompt
          editableCategories={editableCategories}
          onSubmit={resolve}
        />
      )
    )

    if (data === null) return

    void categoryMutations.create({
      variables: {
        input: { ...data },
      },
    })
  }

  async function handleEditPrompt(category: UserDocumentCategoryForSettings) {
    const initialData: UserDocumentCategoryForm = {
      name: category.name,
    }
    const { data } = await addPrompt<UserDocumentCategoryForm | null>(
      resolve => (
        <UserDocumentCategoryPrompt
          initialData={initialData}
          editableCategories={editableCategories}
          onSubmit={resolve}
        />
      )
    )
    if (data === null) return

    void categoryMutations.patch({
      variables: {
        id: category.id,
        input: { ...data },
      },
    })
  }
  function validateName(name: string) {
    if (name === '') return false
    if (editableCategories.find(type => type.name === form.newName)) {
      addToast('warning', translations.validation.nameExists)
      return false
    }
    return true
  }

  function checkNameHasChanged(id: string) {
    const category = editableCategories.find(type => type.id === id)
    if (typeof category === 'undefined') return true

    return category.name !== form.editName
  }

  async function submitEditUserDocumentCategoryName() {
    if (!validateName(form.editName) || editing === null) return
    if (!checkNameHasChanged(editing)) {
      setEditing(null)
      return
    }

    await categoryMutations.patch({
      variables: {
        id: editing,
        input: { name: form.editName },
      },
    })
    setEditing(null)
  }

  async function handleDeletePrompt(category: UserDocumentCategoryForSettings) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: category.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void categoryMutations.delete({
      variables: {
        id: category.id,
      },
    })
  }

  useEffect(() => {
    if (!adding && form.newName !== '') {
      updateForm({
        newName: '',
      })
    }
  }, [adding, form.newName, updateForm])

  useEffect(() => {
    if (editing === null && form.editName !== '') {
      updateForm({ editName: '' })
    }
  }, [editing, form.editName, updateForm])

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox initCollapsed={isMobileOnly}>
        <p>{translations.info}</p>
      </InfoBox>

      <OldSection>
        <header className="mb-4 flex w-full items-center justify-between">
          <SectionHeader loading={loading}>
            {translations.userDocumentCategories}
          </SectionHeader>
          {!isMobileOnly && (
            <OldAddButton adding={adding} onClick={handleCreatePrompt} />
          )}
        </header>
        {isMobileOnly ? (
          <UserDocumentCategorySettingsCard
            editableCategories={editableCategories}
            form={form}
            adding={adding}
            editing={editing}
            onFormChange={handler}
            onUpdateForm={updateForm}
            onAdd={setAdding}
            onAddSubmit={submitNewUserDocumentCategory}
            onEdit={setEditing}
            onEditSubmit={submitEditUserDocumentCategoryName}
            onDelete={handleDeletePrompt}
          />
        ) : (
          <UserDocumentCategorySettingsTable
            editableCategories={editableCategories}
            onEditClick={handleEditPrompt}
            onDelete={handleDeletePrompt}
          />
        )}
      </OldSection>
    </SettingsWrapper>
  )
}
